import { useCallback, useEffect, useMemo } from 'react'

import {
  FormInput,
  FormTextarea,
  resetAllModifiedDataByKey,
  useGetFormData,
} from 'cng-common/features/form'
import { ValidationType } from 'cng-common/features/form/configs'
import { TFormValidationResult } from 'cng-common/features/form/models'
import { FormValidationBuilder } from 'cng-common/features/form/utils/validation'
import { Translation } from 'cng-common/features/translations'
import { useAppDispatch } from 'cng-common/hooks/useAppDispatch'
import { Button } from 'core-components/library/button'

import { BUSINESS_PRIORITY_PROGRAM_OFFERING_OPTIONS } from '../../configs/options'
import { BUSINESS_PRIORITIES_TRANSLATION_KEYS as KEYS } from '../../configs/translations'
import { getInitialBusinessPriorityForm } from '../../lib/getInitialBusinessPriorityForm'
import { IBusinessPriority, IBusinessPriorityForm } from '../../model/types'

import { MarketsFormField } from './fields/MarketsFormField'
import { MultiSegmentedFormField } from './fields/MultiSegmentedFormField'

import styles from './BusinessPriorityForm.module.scss'

interface IBusinessPriorityFormProps {
  /** cng-common form state key; keep stable for the lifetime of one create/edit session. */
  formKey: string
  /** Variant selector: worldwide (WW level) vs market level. */
  isWorldwide: boolean
  /** Current market — used to preselect on Market-variant create. */
  market?: string
  /** Available market short codes (from backend GeoMarkets, ordered). */
  marketOptions: string[]
  /** Present => edit mode (init from record); absent => create mode. */
  initialData?: IBusinessPriority
  /** Disables actions and shows a loader on Save while a submit is in flight. */
  isSubmitting?: boolean
  onSubmit: (form: IBusinessPriorityForm) => void | Promise<void>
  onCancel: () => void
}

export const BusinessPriorityForm = ({
  formKey,
  isWorldwide,
  market,
  marketOptions,
  initialData,
  isSubmitting = false,
  onSubmit,
  onCancel,
}: IBusinessPriorityFormProps) => {
  const dispatch = useAppDispatch()
  const isEdit = Boolean(initialData)

  const resolvedMarketOptions = useMemo(() => {
    if (isWorldwide) {
      return marketOptions
    }

    return market ? [market] : []
  }, [isWorldwide, market, marketOptions])

  const initialForm = useMemo(
    () =>
      getInitialBusinessPriorityForm({
        isWorldwide,
        market,
        initialData,
        marketOptions: resolvedMarketOptions,
      }),
    [isWorldwide, market, initialData, resolvedMarketOptions]
  )

  const [{ formData }] = useGetFormData<IBusinessPriorityForm>(formKey, initialForm)

  // const currentlySetToParams = useMemo(
  //   () => [{ key: '{markets}', value: (formData.markets ?? []).join(', ') }],
  //   [formData.markets]
  // )

  const handleSubmit = useCallback(async () => {
    const validation = FormValidationBuilder.build(formKey)
    const errors = await Promise.all([
      validation.call('name', formData.name),
      validation.call('description', formData.description),
      validation.call('markets', formData.markets),
      validation.call('programOfferings', formData.programOfferings),
    ])

    if (errors.some((error): error is TFormValidationResult => Boolean(error))) {
      return
    }

    await onSubmit(formData)
  }, [formKey, formData, onSubmit])

  useEffect(() => {
    return () => {
      dispatch(resetAllModifiedDataByKey(formKey))
    }
  }, [dispatch, formKey])

  return (
    <div className={styles.root}>
      <div className={styles.body}>
        <FormInput
          isRequired
          formKey={formKey}
          field="name"
          defaultValue={initialForm.name}
          type="text"
          label={KEYS.form.nameLabel}
        />

        <FormTextarea
          isRequired
          formKey={formKey}
          field="description"
          defaultValue={initialForm.description}
          label={KEYS.form.descriptionLabel}
          validationType={ValidationType.ONCHANGE}
          rows={4}
        />

        <MarketsFormField
          isRequired
          isLocked={!isWorldwide}
          formKey={formKey}
          field="markets"
          label={KEYS.form.marketsLabel}
          options={resolvedMarketOptions}
          defaultValue={initialForm.markets}
          requiredMessage={KEYS.form.marketsRequired}
          hint={<Translation text={KEYS.form.marketsHint} span />}
          footer={
            <>
              {/* <Translation TODO: clarify if we need this
                text={KEYS.form.marketsCurrentlySetTo}
                replaceParams={currentlySetToParams}
              /> */}
            </>
          }
        />

        <MultiSegmentedFormField
          isRequired
          formKey={formKey}
          field="programOfferings"
          label={KEYS.form.offeringsLabel}
          options={BUSINESS_PRIORITY_PROGRAM_OFFERING_OPTIONS}
          defaultValue={initialForm.programOfferings}
          requiredMessage={KEYS.form.offeringsRequired}
          hint={<Translation text={KEYS.form.offeringsHint} span />}
        />
      </div>

      <div className={styles.footer}>
        <Button variant="outlined" color="grey" onClick={onCancel} disabled={isSubmitting}>
          <Translation text={KEYS.form.cancel} />
        </Button>

        <Button
          color="primary"
          variant="contained"
          onClick={handleSubmit}
          loading={isSubmitting}
          disabled={isSubmitting}
        >
          <Translation text={isEdit ? KEYS.form.save : KEYS.form.create} />
        </Button>
      </div>
    </div>
  )
}

BusinessPriorityForm.displayName = 'BusinessPriorityForm'

export default BusinessPriorityForm
